import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { authApi } from "../../services/auth";
import {
  Mail,
  LockKeyhole,
  ShieldCheck,
  Eye,
  EyeOff,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  KeyRound,
} from "lucide-react";

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function strengthScore(pw: string) {
  let s = 0;
  if (pw.length >= 6) s++;
  if (pw.length >= 10) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(4, s); // 0..4
}

const pickMessage = (err: any, fallback: string) =>
  err?.response?.data?.message ||
  err?.response?.data?.Message ||
  err?.response?.data?.error ||
  fallback;

export default function RegisterForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Step 1: register
  const [username, setUsername] = useState(""); // ✅ backend cần username
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  // Step 2: OTP confirm email
  const [otp, setOtp] = useState("");
  const [stage, setStage] = useState<"register" | "otp">("register");

  const [showPw, setShowPw] = useState(false);
  const [showCf, setShowCf] = useState(false);

  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState<{
    username?: string;
    email?: string;
    password?: string;
    confirm?: string;
    agree?: string;
    otp?: string;
    form?: string;
  }>({});

  const score = useMemo(() => strengthScore(password), [password]);
  const strengthPct = Math.round((score / 4) * 100);
  const strengthLabel = useMemo(() => {
    if (score <= 1) return t("register.strength.weak");
    if (score === 2) return t("register.strength.fair");
    if (score === 3) return t("register.strength.good");
    return t("register.strength.strong");
  }, [score, t]);

  const inputBase =
    "w-full h-12 rounded-[18px] border bg-brand-ivory/70 backdrop-blur-md " +
    "px-4 text-sm outline-none transition " +
    "placeholder:text-brand-brown/35";

  const inputWithIcon = "pl-11";
  const inputError = "border-red-500/35 focus:border-red-500/55";
  const inputNormal =
    "border-brand-brown/10 focus:border-brand-brown/30 hover:border-brand-brown/20";

  const canSubmitRegister = useMemo(() => {
    return (
      username.trim().length > 0 &&
      email.trim().length > 0 &&
      password.length >= 6 &&
      confirm.length > 0 &&
      confirm === password &&
      agree &&
      !loading
    );
  }, [username, email, password, confirm, agree, loading]);

  const canSubmitOtp = useMemo(() => {
    const v = otp.trim();
    return v.length >= 4 && v.length <= 10 && !loading; // otp có thể 4-6-8 tuỳ bạn
  }, [otp, loading]);

  const validateRegister = () => {
    const e: typeof errors = {};
    const em = email.trim();
    const un = username.trim();

    if (!un) e.username = t("register.errors.usernameRequired");
    else if (un.length < 3)
      e.username = t("register.errors.usernameMin", { n: 3 });

    if (!em) e.email = t("register.errors.emailRequired");
    else if (!/^\S+@\S+\.\S+$/.test(em))
      e.email = t("register.errors.emailInvalid");

    if (!password) e.password = t("register.errors.passwordRequired");
    else if (password.length < 6)
      e.password = t("register.errors.passwordMin", { n: 6 });

    if (!confirm) e.confirm = t("register.errors.confirmRequired");
    else if (confirm !== password)
      e.confirm = t("register.errors.passwordMismatch");

    if (!agree) e.agree = t("register.errors.agreeRequired");

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateOtp = () => {
    const e: typeof errors = {};
    const v = otp.trim();

    if (!v) e.otp = t("register.errors.otpRequired");
    // nếu OTP chỉ số thì bật dòng dưới
    // else if (!/^\d+$/.test(v)) e.otp = t("register.errors.otpNumeric");

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmitRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setErrors((prev) => ({ ...prev, form: undefined }));
    if (!validateRegister()) return;

    try {
      setLoading(true);

      // ✅ payload theo backend
      await authApi.register({
        username: username.trim(),
        email: email.trim().toLowerCase(),
        password,
        roleId: 2, // ⚠️ đổi theo hệ thống của bạn (ví dụ 2 = Customer)
      });

      // ✅ chuyển sang bước nhập OTP
      setStage("otp");
    } catch (err: any) {
      const apiMessage = pickMessage(err, t("register.errors.registerFailed"));

      // backend bạn trả message tiếng Việt: "Email đã được sử dụng."...
      const lower = String(apiMessage).toLowerCase();
      if (lower.includes("email"))
        setErrors((m) => ({ ...m, email: apiMessage }));
      else if (lower.includes("tên đăng nhập") || lower.includes("username"))
        setErrors((m) => ({ ...m, username: apiMessage }));
      else setErrors((m) => ({ ...m, form: apiMessage }));
    } finally {
      setLoading(false);
    }
  };

  const onSubmitOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setErrors((prev) => ({ ...prev, form: undefined }));
    if (!validateOtp()) return;

    try {
      setLoading(true);

      await authApi.confirmEmail(otp.trim());

      // ✅ confirm xong => chuyển login
      navigate("/auth?tab=login", { replace: true });
    } catch (err: any) {
      const apiMessage = pickMessage(err, t("register.errors.otpVerifyFailed"));
      setErrors((m) => ({ ...m, otp: apiMessage }));
    } finally {
      setLoading(false);
    }
  };

  const onGoogleRegister = () => {
    setErrors((prev) => ({
      ...prev,
      form: t("register.errors.googleRegisterNotAvailable"),
    }));
  };

  // UI: stage register / otp
  return stage === "register" ? (
    <form onSubmit={onSubmitRegister} className="space-y-6">
      {/* USERNAME */}
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/55">
          {t("register.usernameLabel")}
        </p>

        <div className="relative">
          <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-brown/40" />
          <input
            type="text"
            placeholder={t("register.usernamePlaceholder")}
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (errors.username)
                setErrors((m) => ({ ...m, username: undefined }));
              if (errors.form) setErrors((m) => ({ ...m, form: undefined }));
            }}
            autoComplete="username"
            className={cn(
              inputBase,
              inputWithIcon,
              errors.username ? inputError : inputNormal,
            )}
          />
        </div>

        {errors.username && (
          <p className="text-xs text-red-600/80">{errors.username}</p>
        )}
      </div>

      {/* EMAIL */}
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/55">
          {t("register.emailLabel")}
        </p>

        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-brown/40" />
          <input
            type="email"
            inputMode="email"
            placeholder={t("register.emailPlaceholder")}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) setErrors((m) => ({ ...m, email: undefined }));
              if (errors.form) setErrors((m) => ({ ...m, form: undefined }));
            }}
            autoComplete="email"
            className={cn(
              inputBase,
              inputWithIcon,
              errors.email ? inputError : inputNormal,
            )}
          />
        </div>

        {errors.email && (
          <p className="text-xs text-red-600/80">{errors.email}</p>
        )}
      </div>

      {/* PASSWORD */}
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/55">
          {t("register.passwordLabel")}
        </p>

        <div className="relative">
          <LockKeyhole className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-brown/40" />
          <input
            type={showPw ? "text" : "password"}
            placeholder="••••••••"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (errors.password)
                setErrors((m) => ({ ...m, password: undefined }));
              if (errors.form) setErrors((m) => ({ ...m, form: undefined }));
            }}
            autoComplete="new-password"
            className={cn(
              inputBase,
              inputWithIcon,
              "pr-12",
              errors.password ? inputError : inputNormal,
            )}
          />

          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center rounded-[14px]
                       border border-brand-brown/10 hover:border-brand-brown/25 hover:bg-brand-brown/5 transition"
            aria-label={
              showPw ? t("register.hidePassword") : t("register.showPassword")
            }
          >
            {showPw ? (
              <EyeOff className="w-4 h-4 text-brand-brown/55" />
            ) : (
              <Eye className="w-4 h-4 text-brand-brown/55" />
            )}
          </button>
        </div>

        {errors.password && (
          <p className="text-xs text-red-600/80">{errors.password}</p>
        )}

        {/* Strength */}
        <div className="pt-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.45em] text-brand-brown/45">
              {t("register.strength.label")}
            </span>
            <span className="text-[10px] uppercase tracking-[0.45em] text-brand-brown/55">
              {strengthLabel}
            </span>
          </div>

          <div className="mt-2 h-[6px] rounded-full border border-brand-brown/10 overflow-hidden bg-brand-ivory">
            <div
              className="h-full bg-brand-gold transition-all"
              style={{ width: `${strengthPct}%` }}
            />
          </div>

          <p className="mt-2 text-xs text-brand-brown/45 leading-relaxed">
            {t("register.strength.hint")}
          </p>
        </div>
      </div>

      {/* CONFIRM */}
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/55">
          {t("register.confirmLabel")}
        </p>

        <div className="relative">
          <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-brown/40" />
          <input
            type={showCf ? "text" : "password"}
            placeholder="••••••••"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
              if (errors.confirm)
                setErrors((m) => ({ ...m, confirm: undefined }));
              if (errors.form) setErrors((m) => ({ ...m, form: undefined }));
            }}
            autoComplete="new-password"
            className={cn(
              inputBase,
              inputWithIcon,
              "pr-12",
              errors.confirm ? inputError : inputNormal,
            )}
          />

          <button
            type="button"
            onClick={() => setShowCf((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center rounded-[14px]
                       border border-brand-brown/10 hover:border-brand-brown/25 hover:bg-brand-brown/5 transition"
            aria-label={
              showCf
                ? t("register.hideConfirmPassword")
                : t("register.showConfirmPassword")
            }
          >
            {showCf ? (
              <EyeOff className="w-4 h-4 text-brand-brown/55" />
            ) : (
              <Eye className="w-4 h-4 text-brand-brown/55" />
            )}
          </button>
        </div>

        {errors.confirm && (
          <p className="text-xs text-red-600/80">{errors.confirm}</p>
        )}

        {confirm.length > 0 && !errors.confirm && confirm === password && (
          <div className="flex items-center gap-2 text-xs text-brand-brown/55">
            <CheckCircle2 className="w-4 h-4 text-brand-gold" />
            {t("register.passwordsMatch")}
          </div>
        )}
      </div>

      {/* TERMS */}
      <div className="space-y-2">
        <label className="flex items-start gap-3 cursor-pointer select-none">
          {/* ✅ hidden native checkbox (vẫn accessible) */}
          <input
            type="checkbox"
            checked={agree}
            onChange={(e) => {
              setAgree(e.target.checked);
              if (errors.agree) setErrors((m) => ({ ...m, agree: undefined }));
              if (errors.form) setErrors((m) => ({ ...m, form: undefined }));
            }}
            className="sr-only"
          />

          {/* ✅ custom box */}
          <span
            className={cn(
              "mt-1 h-4 w-4 rounded-[6px] border transition grid place-items-center",
              agree
                ? "bg-brand-brown border-brand-brown"
                : "bg-transparent border-brand-brown/25",
              errors.agree ? "border-red-500/55" : "",
            )}
            aria-hidden="true"
          >
            {agree && (
              <svg viewBox="0 0 24 24" className="h-3 w-3 text-brand-ivory">
                <path
                  fill="currentColor"
                  d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z"
                />
              </svg>
            )}
          </span>

          <span className="text-xs text-brand-brown/55 leading-relaxed">
            {t("register.agreePrefix")}{" "}
            <a
              href="#"
              className="underline underline-offset-4 hover:text-brand-brown transition"
              onClick={(e) => e.preventDefault()}
            >
              {t("register.terms")}
            </a>{" "}
            {t("register.and")}{" "}
            <a
              href="#"
              className="underline underline-offset-4 hover:text-brand-brown transition"
              onClick={(e) => e.preventDefault()}
            >
              {t("register.privacy")}
            </a>
            .
          </span>
        </label>

        {errors.agree && (
          <p className="text-xs text-red-600/80">{errors.agree}</p>
        )}
      </div>

      {/* FORM ERROR */}
      {errors.form && (
        <div className="rounded-[18px] border border-red-500/20 bg-red-500/5 px-4 py-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-red-600/80 mt-0.5" />
            <p className="text-sm text-red-600/80 leading-relaxed">
              {errors.form}
            </p>
          </div>
        </div>
      )}

      {/* PRIMARY CTA */}
      <button
        type="submit"
        disabled={!canSubmitRegister}
        className={cn(
          "w-full h-12 rounded-[18px] transition",
          "text-[10px] uppercase tracking-[0.55em]",
          loading
            ? "bg-brand-brown/35 text-brand-ivory/80 cursor-not-allowed"
            : canSubmitRegister
              ? "bg-brand-brown text-brand-ivory hover:opacity-95"
              : "bg-brand-brown/15 text-brand-brown/40 cursor-not-allowed",
        )}
      >
        {loading ? (
          <span className="inline-flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t("register.creating")}
          </span>
        ) : (
          t("register.createAccount")
        )}
      </button>

      {/* DIVIDER */}
      <div className="flex items-center gap-4">
        <div className="h-px flex-1 bg-brand-brown/10" />
        <span className="text-[10px] uppercase tracking-[0.45em] text-brand-brown/35">
          {t("register.or")}
        </span>
        <div className="h-px flex-1 bg-brand-brown/10" />
      </div>

      {/* GOOGLE */}
      <button
        type="button"
        onClick={onGoogleRegister}
        disabled={loading}
        className={cn(
          "w-full h-12 rounded-[18px] border transition",
          "text-[10px] uppercase tracking-[0.45em]",
          loading
            ? "border-brand-brown/10 text-brand-brown/30 cursor-not-allowed"
            : "border-brand-brown/12 text-brand-brown/60 hover:text-brand-brown hover:border-brand-brown/25 hover:bg-brand-brown/5",
        )}
      >
        {t("register.continueWithGoogle")}
      </button>

      {/* SWITCH */}
      <div className="pt-2 text-center">
        <Link
          to="/auth?tab=login"
          className="text-[10px] uppercase tracking-[0.45em] text-brand-brown/45 hover:text-brand-brown transition"
        >
          {t("register.alreadyHaveAccount")}
        </Link>
      </div>
    </form>
  ) : (
    // ======================
    // OTP CONFIRM STAGE
    // ======================
    <form onSubmit={onSubmitOtp} className="space-y-6">
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/55">
          {t("register.otp.title")}
        </p>

        <p className="text-xs text-brand-brown/55 leading-relaxed">
          {t("register.otp.subtitle")}{" "}
          <span className="font-medium">{email.trim()}</span>.{" "}
          {t("register.otp.subtitle2")}
        </p>

        <div className="relative">
          <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-brown/40" />
          <input
            inputMode="numeric"
            placeholder={t("register.otp.placeholder")}
            value={otp}
            onChange={(e) => {
              setOtp(e.target.value);
              if (errors.otp) setErrors((m) => ({ ...m, otp: undefined }));
              if (errors.form) setErrors((m) => ({ ...m, form: undefined }));
            }}
            className={cn(
              inputBase,
              inputWithIcon,
              errors.otp ? inputError : inputNormal,
            )}
          />
        </div>

        {errors.otp && <p className="text-xs text-red-600/80">{errors.otp}</p>}
      </div>

      {/* FORM ERROR */}
      {errors.form && (
        <div className="rounded-[18px] border border-red-500/20 bg-red-500/5 px-4 py-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-red-600/80 mt-0.5" />
            <p className="text-sm text-red-600/80 leading-relaxed">
              {errors.form}
            </p>
          </div>
        </div>
      )}

      {/* PRIMARY CTA */}
      <button
        type="submit"
        disabled={!canSubmitOtp}
        className={cn(
          "w-full h-12 rounded-[18px] transition",
          "text-[10px] uppercase tracking-[0.55em]",
          loading
            ? "bg-brand-brown/35 text-brand-ivory/80 cursor-not-allowed"
            : canSubmitOtp
              ? "bg-brand-brown text-brand-ivory hover:opacity-95"
              : "bg-brand-brown/15 text-brand-brown/40 cursor-not-allowed",
        )}
      >
        {loading ? (
          <span className="inline-flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t("register.otp.verifying")}
          </span>
        ) : (
          t("register.otp.verify")
        )}
      </button>

      <button
        type="button"
        disabled={loading}
        onClick={() => {
          // cho user sửa email/username nếu cần
          setStage("register");
          setOtp("");
          setErrors({});
        }}
        className={cn(
          "w-full h-12 rounded-[18px] border transition",
          "text-[10px] uppercase tracking-[0.45em]",
          loading
            ? "border-brand-brown/10 text-brand-brown/30 cursor-not-allowed"
            : "border-brand-brown/12 text-brand-brown/60 hover:text-brand-brown hover:border-brand-brown/25 hover:bg-brand-brown/5",
        )}
      >
        {t("register.otp.backToRegister")}
      </button>

      {/* SWITCH */}
      <div className="pt-2 text-center">
        <Link
          to="/auth?tab=login"
          className="text-[10px] uppercase tracking-[0.45em] text-brand-brown/45 hover:text-brand-brown transition"
        >
          {t("register.otp.alreadyConfirmed")}
        </Link>
      </div>
    </form>
  );
}
